import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { contractsAPI, templatesAPI } from '../services/api'
import type { ContractTemplate } from '../types'

export default function ContractEditor() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const [templates, setTemplates] = useState<ContractTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<ContractTemplate | null>(null)
  const [title, setTitle] = useState('')
  const [clientName, setClientName] = useState('')
  const [price, setPrice] = useState('')
  const [htmlContent, setHtmlContent] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const [templatesResp] = await Promise.all([
          templatesAPI.list(),
        ])
        setTemplates(templatesResp.data.templates || [])

        if (isEditing && id) {
          const contractResp = await contractsAPI.getById(Number(id))
          const contract = contractResp.data
          setTitle(contract.title)
          setClientName(contract.client_company_name || '')
          setPrice(contract.price_amount?.toString() || '')
          setHtmlContent(contract.rendered_html || '')
        }
      } catch (err) {
        console.error('Failed to load editor data', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id])

  const handleTemplateSelect = async (templateId: number) => {
    try {
      const resp = await templatesAPI.getById(templateId)
      setSelectedTemplate(resp.data)
      setHtmlContent(resp.data.html_content)
      setTitle(resp.data.name)
    } catch (err) {
      console.error('Failed to load template', err)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const data = {
        template_id: selectedTemplate?.id,
        title,
        client_company_name: clientName,
        price_amount: price ? parseFloat(price) : null,
        rendered_html: htmlContent,
      }

      if (isEditing && id) {
        await contractsAPI.update(Number(id), data)
      } else {
        const resp = await contractsAPI.create(data)
        navigate(`/contracts/${resp.data.id}`)
        return
      }
      navigate(`/contracts/${id}`)
    } catch (err) {
      console.error('Failed to save contract', err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    )
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <i className="bi bi-file-earmark-text me-2"></i>
          {isEditing ? 'Editar Contrato' : t('contract.new')}
        </h1>
        <div>
          <button className="btn btn-secondary me-2" onClick={() => navigate(-1)}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1"></i>}
            {t('common.save')}
          </button>
        </div>
      </div>

      <div className="row g-3">
        {/* Form */}
        <div className="col-md-8">
          <div className="card mb-3">
            <div className="card-header">Información básica</div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">Plantilla</label>
                <select
                  className="form-select"
                  value={selectedTemplate?.id || ''}
                  onChange={(e) => handleTemplateSelect(Number(e.target.value))}
                  disabled={isEditing}
                >
                  <option value="">Seleccionar plantilla...</option>
                  {templates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Título del contrato</label>
                <input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Cliente</label>
                  <input className="form-control" value={clientName} onChange={(e) => setClientName(e.target.value)} />
                </div>
                <div className="col-md-3">
                  <label className="form-label">Precio (€)</label>
                  <input className="form-control" type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">Contenido del contrato</div>
            <div className="card-body p-0">
              <textarea
                className="form-control border-0 rounded-0"
                style={{ minHeight: '500px', fontFamily: 'monospace', fontSize: '13px' }}
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder="HTML content..."
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">Vista previa</div>
            <div className="card-body p-3" style={{ maxHeight: '80vh', overflow: 'auto' }}>
              <div className="ratio ratio-4x3">
                <iframe
                  srcDoc={htmlContent}
                  title="Preview"
                  style={{ border: 'none', background: 'white', borderRadius: '4px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
